/**
 * @file BackupCodesPanel.tsx
 * @brief The 10 backup codes, shown once (B31): Copy, Download, and "I have
 *        saved my backup codes" before Continue.
 *
 * @details
 * The codes come straight from the setup / regenerate answer and live only
 * in the parent's state; nothing here stores them. Copy and Download run only
 * when pressed. Download is a plain text file, the same blob download the CSV
 * exports use; the Mac app has no download handler, so Electron's default
 * save dialog opens. The Mac app allows clipboard writes for our origin.
 *
 * @author DES
 * @date 25-10-2026
 */

import { useState } from "react";
import { Check, Copy, Download, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { toast } from "@/shared/hooks/useToast";
import { backupCodesFileText } from "./twoFactorRules";

export function BackupCodesPanel({
  codes,
  accountName,
  continueLabel = "Continue",
  busy = false,
  onContinue,
}: {
  codes: string[];
  accountName: string | null;
  continueLabel?: string;
  busy?: boolean;
  onContinue: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ variant: "destructive", title: "Couldn't copy", description: "Select the codes and copy them, or download them." });
    }
  };

  const onDownload = () => {
    const blob = new Blob([backupCodesFileText(codes, accountName)], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "verblizr-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-muted-foreground">
        Each code works once, instead of a code from your app, if you lose your phone. Save them somewhere safe,
        away from your phone. <span className="font-medium text-foreground">You won&rsquo;t see them again.</span>
      </div>
      <ol
        aria-label="Backup codes"
        className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-2xl bg-app-card px-5 py-4 font-mono text-[15px] tracking-wide"
      >
        {codes.map((c) => (
          <li key={c} className="select-all">
            {c}
          </li>
        ))}
      </ol>
      <div className="flex gap-2">
        <Button type="button" variant="secondary" className="flex-1 gap-2" onClick={() => void onCopy()}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button type="button" variant="secondary" className="flex-1 gap-2" onClick={onDownload}>
          <Download className="h-4 w-4" />
          Download
        </Button>
      </div>
      <label className="flex items-start gap-2.5 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-brand"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
        />
        <span>I have saved my backup codes</span>
      </label>
      <Button type="button" className="h-12 w-full text-[15px] font-semibold" disabled={!saved || busy} onClick={onContinue}>
        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : continueLabel}
      </Button>
    </div>
  );
}
